import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveCred, discardCred, deleteCred } from "../redux/actions/credActions";
import { toggleLoading } from "../redux/actions/messageActions";
import { Link } from "react-router-dom";

import Button from "@atlaskit/button/new";
import { ButtonGroup } from "@atlaskit/button";
import ConfirmModal, { ACTION_DELETE, ACTION_DISCARD } from "../common/components/ConfirmModal";

const AuthToolbar = () => {
  const selectedCred = useSelector((state) => state.auth.selectedCred);
  const unsaved_cred = useSelector((state) => state.auth.unsaved_cred);
  const flows = useSelector((state) => state.integration.flows);
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("none");
  const [isUsageExpanded, setIsUsageExpanded] = useState(false);

  const save = () => {
    dispatch(toggleLoading(true, "Saving..."));
    dispatch(saveCred(selectedCred));
  };
  const remove = () => {
    setAction(ACTION_DELETE);
    setOpen(true);
  };
  const discard = () => {
    setAction(ACTION_DISCARD);
    setOpen(true);
  };

  const onAction = () => {
    if (action === ACTION_DELETE) {
      dispatch(deleteCred(selectedCred));
    } else if (action === ACTION_DISCARD) {
      dispatch(discardCred());
    }

    setOpen(false);
    setAction("none");
  };

  const onClose = () => {
    setOpen(false);
    setAction("none");
  };

  useEffect(() => {
    setIsUsageExpanded(false);
  }, [selectedCred?.id]);

  const credentialUsage = useMemo(() => {
    if (!selectedCred?.id || !Array.isArray(flows)) {
      return [];
    }

    return flows.reduce((acc, flow, flowIndex) => {
      const matchingActions = Array.isArray(flow?.actions)
        ? flow.actions.filter((action) => action?.credentialsReference?.value === selectedCred.id)
        : [];

      if (!matchingActions.length) {
        return acc;
      }

      acc.push({
        flowKey: flow?.id || flow?.name || `flow-${flowIndex}`,
        flowId: flow?.id,
        flowName: flow?.name || flow?.id || "Untitled flow",
        actions: matchingActions.map((action, actionIndex) => ({
          actionKey: action?.id || action?.name || `action-${flowIndex}-${actionIndex}`,
          actionId: action?.id,
          actionName: action?.name || action?.id || "Untitled action",
        })),
      });
      return acc;
    }, []);
  }, [flows, selectedCred?.id]);

  const usageActionCount = credentialUsage.reduce((total, flow) => total + flow.actions.length, 0);
  const hasSavedCredential = Boolean(selectedCred?.id);

  return (
    <>
      <ButtonGroup>
        <Button appearance="primary" isDisabled={!unsaved_cred} onClick={save}>
          Save
        </Button>
        <Button appearance="danger" isDisabled={!unsaved_cred} onClick={discard}>
          Discard
        </Button>
        <Button appearance="danger" onClick={remove}>
          Delete
        </Button>
      </ButtonGroup>
      <br />
      <div className="credential-usage-section" style={{ marginTop: 8 }}>
        <Button appearance="subtle" spacing="compact" onClick={() => setIsUsageExpanded((prev) => !prev)}>
          {isUsageExpanded ? "Hide credential usage" : "Show credential usage"}
        </Button>
        {isUsageExpanded && (
          <div
            className="credential-usage-panel"
            style={{
              marginTop: 8,
              border: "1px solid #DFE1E6",
              borderRadius: 3,
              padding: 12,
              background: "#F4F5F7",
            }}
          >
            {!hasSavedCredential ? (
              <p style={{ marginBottom: 0 }}>Save the credential to see which flows are using it.</p>
            ) : credentialUsage.length === 0 ? (
              <p style={{ marginBottom: 0 }}>This credential is not referenced by any integration flows.</p>
            ) : (
              <>
                <p style={{ marginTop: 0 }}>
                  Used by {credentialUsage.length} flow{credentialUsage.length === 1 ? "" : "s"} ({usageActionCount} action
                  {usageActionCount === 1 ? "" : "s"}).
                </p>
                <ul style={{ marginBottom: 0 }}>
                  {credentialUsage.map((flow) => (
                    <li key={flow.flowKey}>
                      <strong>
                        {flow.flowId ? (
                          <Link
                            to={`/integration/${flow.flowId}`}
                            className="atlassian-link"
                            style={{ color: "var(--ds-link, #1677ff)" }}
                          >
                            {flow.flowName}
                          </Link>
                        ) : (
                          flow.flowName
                        )}
                      </strong>
                      <ul style={{ marginTop: 4 }}>
                        {flow.actions.map((action) => (
                          <li key={action.actionKey}>
                            {flow.flowId && action.actionId ? (
                              <Link
                                to={`/integration/${flow.flowId}/action/${action.actionId}`}
                                className="atlassian-link"
                                style={{ color: "var(--ds-link, #1677ff)" }}
                              >
                                {action.actionName}
                              </Link>
                            ) : (
                              action.actionName
                            )}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
      <ConfirmModal
        action={action}
        targetName={selectedCred.name}
        targetId={selectedCred.id}
        isOpen={open}
        onAction={onAction}
        onClose={onClose}
      />
    </>
  );
};
export default AuthToolbar;
